import type { BannerTone } from "@/components/PBanner/PBanner";

/**
 * Escalation ladder for "your plan is about to end" notices.
 *
 * The rung is an ORDINAL, not a boolean: a dismissal stores the rung it
 * silenced, so crossing a threshold (12 days → 3 days, or a renewal payment
 * failing) re-arms the strip by itself. A plain "dismissed today" flag meant a
 * wave-away two weeks out also silenced the day the money failed.
 *
 * Note `useExpiringSubscription` drops anything with `daysRemaining < 0`, so
 * "already expired" is unreachable — day 0 ("ends today") and `pastDue` are
 * the terminal states we can actually paint.
 */
export type ExpiryRung = "none" | "notice" | "warning" | "urgent" | "critical";

export const RUNG_ORDINAL: Record<ExpiryRung, number> = {
  none: 0,
  notice: 1,
  warning: 2,
  urgent: 3,
  critical: 4,
};

export const RUNG_TONE: Record<ExpiryRung, BannerTone> = {
  none: "neutral",
  notice: "info",
  warning: "warning",
  urgent: "warning",
  critical: "danger",
};

/**
 * The last two rungs are not dismissible: at three days out (or once a payment
 * has already failed) losing the paid features is imminent, and a strip the
 * user can wave away is not a warning any more.
 */
export const RUNG_DISMISSIBLE: Record<ExpiryRung, boolean> = {
  none: false,
  notice: true,
  warning: true,
  urgent: false,
  critical: false,
};

export function expiryRung(daysRemaining: number, pastDue = false): ExpiryRung {
  if (pastDue) return "critical"; // money already failed — outranks any date
  if (daysRemaining <= 0) return "critical"; // ends today
  if (daysRemaining <= 3) return "urgent";
  if (daysRemaining <= 7) return "warning";
  return "notice"; // 8–14, the hook's outer window
}
